import React, { useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { accountingApiEvents, accountingApiSelectors } from '../../../api/accounting'
import { pathOr } from 'ramda'
import ViewStatementProvider from '../ViewStatement/ViewStatementProvider'
import ViewStatement from '../ViewStatement/ViewStatement'
import { uiEvents } from '../../../ui'
import { RootState } from 'src/state/types'

type StatementFormat = 'json' | 'pdf' | 'csv'

const STATEMENT_EVENTS = {
  json: accountingApiEvents.bankStatementJson_request,
  pdf: accountingApiEvents.bankStatementPdf_request,
  csv: accountingApiEvents.bankStatementCsv_request,
}

const BankStatement = (): React.ReactElement => {
  const dispatch = useDispatch()

  const fetchBankStatement = useCallback(
    (startDate: string, endDate: string, format: StatementFormat = 'json') => {
      dispatch(
        STATEMENT_EVENTS[format]({
          start_date: startDate,
          end_date: endDate,
        }),
      )
    },
    [dispatch],
  )

  const onClose = useCallback(() => dispatch(uiEvents.redirect('/dashboard')), [dispatch])

  const fetchBankStatementPdf = useCallback(
    (startDate: string, endDate: string) => fetchBankStatement(startDate, endDate, 'pdf'),
    [fetchBankStatement],
  )

  const fetchBankStatementCsv = useCallback(
    (startDate: string, endDate: string) => fetchBankStatement(startDate, endDate, 'csv'),
    [fetchBankStatement],
  )

  const statementData = useSelector((state: RootState) => accountingApiSelectors.getBankStatementJson(state))
  const statementPdf = useSelector((state: RootState) => accountingApiSelectors.getBankStatementPdf(state))
  const statementCsv = useSelector((state: RootState) => accountingApiSelectors.getBankStatementCsv(state))
  const fileName: string = `${pathOr('', ['context', 'agency', 'name'], statementData) as string} Bank Statement`

  return useMemo(() => {
    return (
      <ViewStatementProvider
        isOpen
        onClose={onClose}
        onParamChange={fetchBankStatement}
        statementData={statementData}
        downloadStatement={fetchBankStatementPdf}
        statementPdf={statementPdf}
        pdfFileName={fileName}
        downloadStatementCsv={fetchBankStatementCsv}
        statementCsv={statementCsv}
        csvFileName={fileName}
      >
        <ViewStatement title="Transactional Account Statement" />
      </ViewStatementProvider>
    )
  }, [
    onClose,
    fetchBankStatement,
    fetchBankStatementPdf,
    fetchBankStatementCsv,
    statementData,
    statementPdf,
    statementCsv,
    fileName,
  ])
}

export default BankStatement
